/**
 * Repository-menu in de Editor-header — verzamelt workspace-level bulk-
 * acties die niet aan selectie of canvas-context gebonden zijn:
 *
 *  1. Ontdubbel alle gelijknamige objecten van hetzelfde type/stereotype
 *     (bulk-merge via de bestaande merge-endpoint).
 *  2. Werk vertalingen bij — vul ontbrekende NL/EN-zijde van bilingual
 *     velden aan via de translate-API.
 *  3. Import AMEFF (Open Group ArchiMate XML) in de huidige workpackage.
 *  4. Import JSON-export-bestand in de huidige workpackage.
 *
 * Voor elke bulk-actie wordt EERST een safety-snapshot gemaakt zodat de
 * gebruiker terug kan rollen via Settings → Snapshots als 't onverwachte
 * impact had.
 */

import type { BilingualText, Concept } from "@ae/domain";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, api, type OperationPreview, type SnapshotMetadata } from "../api/client";
import { getRegistry } from "../registry";
import { AmeffLabelReview } from "../settings/AmeffLabelReview";
import { announceThenWait, finishOperation, impactFromPreview } from "../state/announceFirst";
import { useSession } from "../state/session";
import { useUi } from "../state/ui";
import { useWorkspaceData } from "../state/workspace";
import { ImportingModal } from "./ImportingModal";
import { OperationPreviewDialog } from "./OperationPreviewDialog";
import { WorkpackageSettingsDialog } from "./WorkpackageSettingsDialog";

interface AmeffReviewLabel {
  concept_id: string;
  name: string;
  type_id: string;
  stereotypes: string[];
  source: "heuristic" | "unclassified";
}

interface AmeffReviewDiagram {
  diagram_id: string;
  name: string;
  view_kind: string;
  source: "heuristic";
}

interface AmeffReviewAmbiguous {
  concept_id: string;
  name: string;
  type_id: string;
  stereotype: string;
  legacy_type_id: string;
  candidates: string[];
}

interface RepositoryMenuProps {
  workspaceId: string | null;
  branchId: string | null;
}

export function RepositoryMenu({ workspaceId, branchId }: RepositoryMenuProps) {
  const { t } = useTranslation();
  // Repository-menu acties horen WP-scoped te zijn (gebruiker werkt
  // visueel in een specifieke workpackage). Workspace-wide exports
  // moeten naar Settings → Export/Import. Sinds 2026-05-25.
  const workpackageId = useSession((s) => s.workpackageId);
  // Alleen kijken: de instellingen (alleen-lezen), de begrippenlijst en de
  // type-verkenner blijven; snapshots, opschonen, vertalen, import en ook
  // export niet (Gerald, 2026-10-17).
  const readOnly = useSession((s) => s.readOnly);
  const openGlossary = useUi((s) => s.openGlossary);
  const setTypeExplorerOpen = useUi((s) => s.setTypeExplorerOpen);

  const [open, setOpen] = useState(false);
  const [wpSettingsOpen, setWpSettingsOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);
  /**
   * De droge run die aan de aanvrager wordt voorgelegd, met de belofte die op
   * z'n antwoord wacht. Zo blijft `announceThenWait` onwetend van schermen: 't
   * krijgt alleen een functie die "ja" of "nee" teruggeeft.
   */
  const [previewVraag, setPreviewVraag] = useState<{
    preview: OperationPreview;
    label: string;
    beantwoord: (akkoord: boolean) => void;
  } | null>(null);

  const vraagOmAkkoord = (label: string) => (preview: OperationPreview) =>
    new Promise<boolean>((resolve) => {
      setPreviewVraag({
        preview,
        label,
        beantwoord: (akkoord) => {
          setPreviewVraag(null);
          resolve(akkoord);
        },
      });
    });
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreItems, setRestoreItems] = useState<
    Array<{
      id: string;
      label: string;
      created_at: string;
      counts: { concepts: number; relations: number; diagrams: number };
      restorable?: SnapshotMetadata["restorable"];
    }>
  >([]);
  const [restoreScope, setRestoreScope] = useState<string>("");
  // Post-AMEFF-import review-modal state — overlay opent automatisch
  // wanneer de import labels of view-kind-classificaties retourneert.
  const [ameffLabels, setAmeffLabels] = useState<AmeffReviewLabel[]>([]);
  const [ameffDiagrams, setAmeffDiagrams] = useState<AmeffReviewDiagram[]>([]);
  const [ameffAmbiguous, setAmeffAmbiguous] = useState<AmeffReviewAmbiguous[]>([]);
  const [ameffReviewBranchId, setAmeffReviewBranchId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const ameffInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // `window`-level capture i.p.v. `document`-level: empirisch (DevTools-
    // test 2026-05-25) blijkt React Flow tussen window-capture en
    // document-capture pointerdown te onderscheppen — vermoedelijk via
    // een document-capture-listener met stopImmediatePropagation. Window
    // capture is vroeger in de event-chain en wordt wel bereikt.
    // `pointerdown` i.p.v. `mousedown`: pointer-events worden niet
    // gesuprimeerd door RF's pointerdown.preventDefault, terwijl mousedown
    // (de gesynthetiseerde compat-event) dat wel is.
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  // F8-shortcut bridge: Editor.tsx dispatcht `ae:open-repository-menu`.
  // We luisteren hier zodat het menu vanuit de keyboard kan openklappen
  // zonder dat Editor.tsx ons interne state hoeft te kennen.
  useEffect(() => {
    const onOpenEvent = () => setOpen(true);
    window.addEventListener("ae:open-repository-menu", onOpenEvent);
    return () => window.removeEventListener("ae:open-repository-menu", onOpenEvent);
  }, []);

  const enabled = !!workspaceId && !!branchId && !busy;

  const snapshotFirst = async (label: string): Promise<boolean> => {
    if (!workspaceId || !branchId) return false;
    if (useSession.getState().readOnly) return false;
    try {
      // Gebruik workpackage-scoped snapshot zodat we niet de hele
      // workspace overschrijven bij latere restore. `label` wordt
      // server-side genegeerd — die genereert 'm uit de scope-naam.
      // Eigen label-prefix kunnen we niet meegeven; nemen 't op in
      // message zodat 't terugvindbaar is in de snapshots-lijst.
      await api.createWorkpackageSnapshot(workspaceId, branchId, {
        message: `Automatisch gemaakt vóór ${label.toLowerCase()}.`,
      });
      return true;
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(t("repositoryMenu.snapshotFailed", { msg }));
      return false;
    }
  };

  const makeSnapshotManual = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    setBusy("snapshot");
    try {
      const res = await api.createWorkpackageSnapshot(workspaceId, branchId, {});
      setLastResult(
        res.reused
          ? t("repositoryMenu.snapshotReused", { label: res.snapshot.label })
          : t("repositoryMenu.snapshotMade", {
              concepts: res.snapshot.counts.concepts,
              relations: res.snapshot.counts.relations,
              diagrams: res.snapshot.counts.diagrams,
              viewStates: res.snapshot.counts.viewStates ?? 0,
            }),
      );
      setOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(msg);
    } finally {
      setBusy(null);
    }
  };

  const openRestoreModal = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    setBusy("snapshot-list");
    try {
      const res = await api.listWorkpackageSnapshots(workspaceId, branchId);
      setRestoreItems(
        res.items.map((s) => ({
          id: s.id,
          label: s.label,
          created_at: s.created_at,
          counts: s.counts,
          restorable: s.restorable,
        })),
      );
      const scopeLabel =
        res.scope.kind === "folder"
          ? `Folder "${res.scope.folder_name ?? "?"}"`
          : res.scope.kind === "branch"
            ? `Branch "${res.scope.branch_name}"`
            : `WP "${res.scope.workpackage_name}"`;
      setRestoreScope(scopeLabel);
      setRestoreOpen(true);
      setOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(msg);
    } finally {
      setBusy(null);
    }
  };

  /** Opruimen van een snapshot die niet meer terug kan — alleen op verzoek,
   *  met bevestiging (restore-slot-per-branch.md B6). */
  const removeSnapshot = async (snapId: string, label: string) => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    if (!window.confirm(t("repositoryMenu.snapshotCleanupConfirm", { label }))) return;
    setBusy("snapshot-cleanup");
    try {
      await api.deleteWorkpackageSnapshot(workspaceId, branchId, snapId);
      setRestoreItems((items) => items.filter((i) => i.id !== snapId));
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const doRestore = async (snapId: string, label: string) => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    if (!window.confirm(t("repositoryMenu.snapshotRestoreConfirm", { label }))) return;
    setBusy("restore");
    try {
      const preview = await api.dryRunRestoreWorkpackage(workspaceId, branchId, snapId);
      // Een branch-snapshot zet ook de branches eronder terug: nieuwere gaan
      // weg, verdwenen komen terug. Dat hoort de gebruiker vooraf te weten.
      const structuur = preview.branch_check?.structure;
      if (structuur && (structuur.remove.length > 0 || structuur.recreate.length > 0)) {
        const regels = [
          ...structuur.remove.map((b) =>
            t("repositoryMenu.restoreBranchRemoved", { name: b.name }),
          ),
          ...structuur.recreate.map((b) =>
            t("repositoryMenu.restoreBranchRecreated", { name: b.name }),
          ),
        ];
        if (!window.confirm(`${t("repositoryMenu.restoreStructureIntro")}\n\n${regels.join("\n")}`))
          return;
      }
      const uitkomst = await announceThenWait(workspaceId, branchId, {
        kind: "restore",
        label,
        // De server leidt de scope af uit de snapshot: bij een branch-snapshot
        // alleen die branch en z'n deelboom, zodat main niet stil hoeft.
        snapshotId: snapId,
        impact: impactFromPreview(preview),
        preview,
        askPreview: vraagOmAkkoord(label),
      });
      if (!uitkomst.go) {
        setLastResult(t("repositoryMenu.restoreCancelled"));
        return;
      }
      let res: Awaited<ReturnType<typeof api.restoreWorkpackageSnapshot>>;
      try {
        res = await api.restoreWorkpackageSnapshot(workspaceId, branchId, snapId);
      } finally {
        await finishOperation(workspaceId, branchId, uitkomst.operationId);
      }
      setLastResult(
        t("repositoryMenu.snapshotRestored", {
          concepts: res.restored.concepts,
          relations: res.restored.relations,
          diagrams: res.restored.diagrams,
          viewStates: res.restored.viewStates ?? 0,
        }),
      );
      setRestoreOpen(false);
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(msg);
    } finally {
      setBusy(null);
    }
  };

  const dedupeAll = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    if (!window.confirm(t("repositoryMenu.dedupeConfirm"))) return;
    setBusy("dedupe");
    try {
      const snapOk = await snapshotFirst(t("repositoryMenu.snapPreDedupe"));
      if (!snapOk) return;
      const ws = useWorkspaceData.getState();
      const concepts = ws.store.listConcepts();
      const groups = new Map<string, Concept[]>();
      for (const c of concepts) {
        const name = (c.name.nl ?? c.name.en ?? "").trim().toLowerCase();
        if (!name) continue;
        // `folder.ReferenceObject` strippen uit group-key: dit is een
        // marker-stereotype dat alleen op Base-folder-concepten staat
        // (setConceptFolder voegt 'm toe). Zonder strippen krijg je
        // verschillende group-keys voor folder-versie en non-folder-
        // copy van eenzelfde object — dedupe zou ze dan niet meer als
        // duplicaten zien en de gewenste "merge non-Base op Base-
        // folder"-flow zou nooit triggeren.
        const stereoKey = [...c.stereotypes]
          .filter((s) => s !== "folder.ReferenceObject")
          .sort()
          .join(",");
        const key = `${c.typeId}|${stereoKey}|${name}`;
        const arr = groups.get(key) ?? [];
        arr.push(c);
        groups.set(key, arr);
      }
      let mergedGroups = 0;
      let mergedLosers = 0;
      for (const [, arr] of groups) {
        if (arr.length < 2) continue;
        // Winner-keuze: een concept in een Base-folder krijgt voorrang.
        // Folders bestaan alleen in Base-WP (zie folders.ts:69 +
        // ensureBaseWorkpackage); dus folderId != null = library-/
        // template-concept dat we als canonieke variant willen behouden.
        // Non-folder duplicaten worden ernaar gemerged. Zonder
        // folder-concept in de groep blijft het oude gedrag (eerste
        // wint) — typisch een verzameling toevallig-gelijkenamige
        // gebruiker-concepten zonder library-anker.
        const winner = arr.find((c) => c.folderId != null) ?? arr[0]!;
        const losers = arr.filter((c) => c.id !== winner.id).map((c) => c.id);
        if (losers.length === 0) continue;
        await ws.mergeConcepts(workspaceId, branchId, winner.id, losers);
        mergedGroups++;
        mergedLosers += losers.length;
      }
      setLastResult(
        mergedGroups === 0
          ? t("repositoryMenu.dedupeDoneNone")
          : t("repositoryMenu.dedupeDone", { groups: mergedGroups, losers: mergedLosers }),
      );
      setOpen(false);
      // In-memory workspace-store re-hydraten zodat UI (Foldertree,
      // Explorer, tabs) de gemergede counts toont zonder hard-refresh.
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    } finally {
      setBusy(null);
    }
  };

  const autoDetectNesting = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    if (!window.confirm(t("repositoryMenu.autoNestConfirm"))) return;
    setBusy("autoNest");
    try {
      const snapOk = await snapshotFirst(t("repositoryMenu.snapPreAutoNest"));
      if (!snapOk) return;
      const ws = useWorkspaceData.getState();
      const result = ws.autoDetectNestingOnDiagram(workspaceId, branchId, "all");
      setLastResult(
        result.nestedCount === 0
          ? t("repositoryMenu.autoNestDoneNone")
          : t("repositoryMenu.autoNestDone", {
              count: result.nestedCount,
              diagrams: result.diagramsAffected,
            }),
      );
      setOpen(false);
      // Geen full hydrate nodig — autoDetectNestingOnDiagram bumpt al
      // de revision en persist via schedulePersistDiagram.
    } finally {
      setBusy(null);
    }
  };

  // Backfill: zet model-eigenaarschap (parent_id) op Sharp-attributen/identifiers
  // op basis van hun viewState-nesting onder een «Concept» (BusinessObject).
  // Eenmalig nodig voor data die vóór de parent_id-koppeling is gemaakt.
  const linkAttributesToConcepts = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    if (!window.confirm(t("repositoryMenu.linkAttrConfirm"))) return;
    setBusy("linkAttr");
    try {
      const snapOk = await snapshotFirst(t("repositoryMenu.snapPreLinkAttr"));
      if (!snapOk) return;
      const ws = useWorkspaceData.getState();
      const store = ws.store;
      const owner = new Map<string, string>();
      for (const d of store.listDiagrams()) {
        const vs = d.viewState;
        if (!vs) continue;
        for (const [k, ve] of Object.entries(vs)) {
          if (!ve.containerId) continue;
          const childId = ve.ref ?? k;
          if (owner.has(childId)) continue;
          const child = store.getConcept(childId);
          if (
            !child ||
            (child.typeId !== "sharp.Attribute" && child.typeId !== "sharp.Identifier")
          ) {
            continue;
          }
          const parentVe = vs[ve.containerId];
          const parentId = parentVe?.ref ?? ve.containerId;
          const parentC = store.getConcept(parentId);
          // Subtype-bewust: ook subtypes van BusinessObject.
          if (
            parentC &&
            getRegistry().effectiveTypeIdsFor(parentC.typeId).has("archimate.BusinessObject")
          ) {
            owner.set(childId, parentId);
          }
        }
      }
      let linked = 0;
      for (const [childId, parentId] of owner) {
        const c = store.getConcept(childId);
        if (c && c.parentId !== parentId) {
          await ws.patchConcept(workspaceId, branchId, childId, { parent_id: parentId });
          linked++;
        }
      }
      setLastResult(
        linked === 0
          ? t("repositoryMenu.linkAttrDoneNone")
          : t("repositoryMenu.linkAttrDone", { count: linked }),
      );
      setOpen(false);
      await ws.hydrate(workspaceId, branchId);
    } finally {
      setBusy(null);
    }
  };

  const updateTranslations = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    if (!window.confirm(t("repositoryMenu.translateConfirm"))) return;
    setBusy("translate");
    try {
      const snapOk = await snapshotFirst(t("repositoryMenu.snapPreTranslate"));
      if (!snapOk) return;
      const ws = useWorkspaceData.getState();
      const reg = getRegistry();
      const concepts = ws.store.listConcepts();
      const diagrams = ws.store.listDiagrams();
      let filledCount = 0;
      const touchedConcepts = new Set<string>();
      const touchedDiagrams = new Set<string>();
      let errMsg = "";

      // Helper: bepaal welke kant ontbreekt + roep translate aan. Patch
      // concept met de aangevulde bilingual-waarde. Geeft 1 terug bij
      // succesvolle vulling, 0 wanneer beide gevuld of beide leeg waren.
      //
      // Swap-detect (2026-05-23): wanneer Claude exact dezelfde tekst
      // teruggeeft als de bron (case-insensitive trim-match), was de
      // bron-taal-aanname mis — de tekst was eigenlijk al in de
      // target-taal. We doen dan een tweede translate met omgekeerde
      // richting om de bron-taal-versie te krijgen, en swappen de
      // velden. Zo herstelt deze functie automatisch van
      // PlantUML-paste-flows waar de modelleur z'n UI-taal verschilde
      // van de PlantUML-snippet-taal. Caveat: eigennamen en korte
      // tokens (Apple, CRM, OK) zijn legitiem identiek in beide talen
      // → swap-detect triggert false-positive met als resultaat
      // beide kanten dezelfde tekst. Functioneel OK, wel één
      // verspilde Claude-call.
      const fillSide = async (
        text: BilingualText | undefined,
        kind: "name" | "description",
      ): Promise<{ next: BilingualText; filled: boolean } | null> => {
        const nl = (text?.nl ?? "").trim();
        const en = (text?.en ?? "").trim();
        if ((nl !== "" && en !== "") || (nl === "" && en === "")) return null;
        const source: "nl" | "en" = nl !== "" ? "nl" : "en";
        const target: "nl" | "en" = source === "nl" ? "en" : "nl";
        const sourceText = source === "nl" ? nl : en;
        try {
          const res = await api.translate({
            source_lang: source,
            target_lang: target,
            text: sourceText,
            kind,
          });
          const identical = res.text.trim().toLowerCase() === sourceText.trim().toLowerCase();
          if (identical) {
            // Bron-taal-aanname was mis: doe omgekeerde translate.
            // Het "source"-veld bevatte eigenlijk target-taal-tekst.
            try {
              const swap = await api.translate({
                source_lang: target,
                target_lang: source,
                text: sourceText,
                kind,
              });
              return {
                next: { ...(text ?? {}), [source]: swap.text, [target]: sourceText },
                filled: true,
              };
            } catch {
              // Swap-call faalde — val terug op de eerste (identieke) translate.
              return {
                next: { ...(text ?? {}), [source]: sourceText, [target]: res.text },
                filled: true,
              };
            }
          }
          return {
            next: { ...(text ?? {}), [source]: sourceText, [target]: res.text },
            filled: true,
          };
        } catch (err) {
          errMsg = err instanceof ApiError ? err.message : String(err);
          return null;
        }
      };

      for (const concept of concepts) {
        let patched = false;
        let nextName = concept.name;
        let nextDescription = concept.description;
        const nextAttributes: Record<string, unknown> = { ...concept.attributes };

        // Naam
        const nameUpdate = await fillSide(concept.name, "name");
        if (nameUpdate) {
          nextName = nameUpdate.next;
          filledCount++;
          patched = true;
        }

        // Beschrijving
        const descUpdate = await fillSide(concept.description, "description");
        if (descUpdate) {
          nextDescription = descUpdate.next;
          filledCount++;
          patched = true;
        }

        // Stereotype-attributen met type bilingual_string. Eerst alle
        // toepasselijke schema's verzamelen, dan elk doorlopen.
        const schemas: { name: string }[] = [];
        for (const stId of concept.stereotypes) {
          const st = reg.getStereotype(stId);
          if (!st) continue;
          for (const a of reg.resolveStereotypeAttributes(st)) {
            const baseType = a.type.endsWith("?") ? a.type.slice(0, -1) : a.type;
            if (baseType === "bilingual_string") schemas.push({ name: a.name });
          }
        }
        for (const s of schemas) {
          const cur = nextAttributes[s.name];
          if (cur === undefined || cur === null) continue;
          if (typeof cur !== "object") continue;
          const bi = cur as BilingualText;
          const update = await fillSide(bi, "description");
          if (update) {
            nextAttributes[s.name] = update.next;
            filledCount++;
            patched = true;
          }
        }

        if (patched) {
          touchedConcepts.add(concept.id);
          try {
            await useWorkspaceData.getState().patchConcept(workspaceId, branchId, concept.id, {
              name: nextName,
              description: nextDescription,
              attributes: nextAttributes,
            });
          } catch (err) {
            errMsg = err instanceof ApiError ? err.message : String(err);
          }
        }
      }

      // Diagrammen — analoog aan concepten: vul ontbrekende NL/EN-zijde
      // van zowel name als description (beide bilingual). Diagram
      // attributes hebben geen bilingual_string-schema's dus die
      // hoeven we niet te scannen.
      for (const diagram of diagrams) {
        const nameUpdate = await fillSide(diagram.name, "name");
        const descUpdate = await fillSide(diagram.description, "description");
        if (!nameUpdate && !descUpdate) continue;
        touchedDiagrams.add(diagram.id);
        try {
          if (nameUpdate) {
            await useWorkspaceData
              .getState()
              .renameDiagram(workspaceId, branchId, diagram.id, nameUpdate.next);
            filledCount++;
          }
          if (descUpdate) {
            await useWorkspaceData
              .getState()
              .setDiagramDescription(workspaceId, branchId, diagram.id, descUpdate.next);
            filledCount++;
          }
        } catch (err) {
          errMsg = err instanceof ApiError ? err.message : String(err);
        }
      }

      if (errMsg) {
        setLastResult(t("repositoryMenu.translateFailed", { msg: errMsg }));
      } else {
        setLastResult(
          t("repositoryMenu.translateDone", {
            filled: filledCount,
            concepts: touchedConcepts.size,
            diagrams: touchedDiagrams.size,
          }),
        );
      }
      setOpen(false);
      // Re-hydrate zodat de UI de bijgewerkte bilingual-velden toont
      // (anders zien gebruikers in EN-modus nog de oude EN-leeg-status).
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    } finally {
      setBusy(null);
    }
  };

  /** Trigger een browser-download van een Blob met de gegeven naam.
   *  Gedeeld pad tussen JSON- en AMEFF-export. */
  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /** Export huidige branch (main of feature) als JSON, **scoped op de
   *  actieve workpackage**. Het Repository-menu opereert visueel binnen
   *  een specifieke WP, dus de export volgt die scope. Workspace-wide
   *  exports (alle WPs in één bestand) zitten in Settings → Export/Import. */
  const exportJson = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    setBusy("export-json");
    try {
      const { blob, filename } = await api.exportWorkspace(
        workspaceId,
        branchId,
        workpackageId ? { workpackageId } : undefined,
      );
      downloadBlob(blob, filename);
      setLastResult(t("repositoryMenu.exportDone", { filename, size: formatBytes(blob.size) }));
      setOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(t("repositoryMenu.exportFailed", { msg }));
    } finally {
      setBusy(null);
    }
  };

  /** Export huidige branch als ArchiMate Open Exchange XML, scoped op
   *  de actieve workpackage (zelfde patroon als exportJson). */
  const exportAmeff = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    setBusy("export-ameff");
    try {
      const { blob, filename, skipped } = await api.exportAmeff(
        workspaceId,
        branchId,
        workpackageId ? { workpackageId } : undefined,
      );
      downloadBlob(blob, filename);
      // Overgeslagen concepten expliciet melden. AMEFF kent geen equivalent
      // voor workspace-subtypes, dus die gaan niet mee — dat hoort de
      // modelleur te weten vóór 'ie het bestand doorstuurt, niet erna.
      const base = t("repositoryMenu.exportDone", { filename, size: formatBytes(blob.size) });
      setLastResult(
        skipped
          ? `${base} ${t("repositoryMenu.exportSkipped", {
              count: String(skipped.count),
              types: skipped.typeIds.join(", "),
            })}`
          : base,
      );
      setOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(t("repositoryMenu.exportFailed", { msg }));
    } finally {
      setBusy(null);
    }
  };

  const handleAmeffPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    setBusy("ameff");
    try {
      const snapOk = await snapshotFirst(t("repositoryMenu.snapPreAmeff"));
      if (!snapOk) return;
      const xml = await file.text();
      // AMEFF ontleedt alleen; die import voegt altijd toe met verse UUID's,
      // dus de voorspelling is "wat komt erbij" en niet "wat raak ik kwijt".
      const previewAmeff = await api.dryRunImportAmeff(workspaceId, branchId, xml);
      const uitkomstAmeff = await announceThenWait(workspaceId, branchId, {
        kind: "import",
        label: file.name,
        ...(workpackageId ? { workpackageIds: [workpackageId] } : {}),
        impact: impactFromPreview(previewAmeff),
        preview: previewAmeff,
        askPreview: vraagOmAkkoord(file.name),
      });
      if (!uitkomstAmeff.go) {
        setLastResult(t("repositoryMenu.importCancelled"));
        return;
      }
      let result: Awaited<ReturnType<typeof api.importAmeff>>;
      try {
        result = await api.importAmeff(workspaceId, branchId, xml);
      } finally {
        await finishOperation(workspaceId, branchId, uitkomstAmeff.operationId);
      }
      const parts: string[] = [];
      if (result.concepts_inserted > 0)
        parts.push(`${result.concepts_inserted} ${t("repositoryMenu.unitConcepts")}`);
      if (result.relations_inserted > 0)
        parts.push(`${result.relations_inserted} ${t("repositoryMenu.unitRelations")}`);
      if (result.diagrams_inserted > 0)
        parts.push(`${result.diagrams_inserted} ${t("repositoryMenu.unitDiagrams")}`);
      if (result.view_states_written && result.view_states_written > 0)
        parts.push(`${result.view_states_written} ${t("repositoryMenu.unitViewStates")}`);
      const skipNotes: string[] = [];
      if (result.concepts_skipped.length > 0)
        skipNotes.push(
          `${result.concepts_skipped.length} ${t("repositoryMenu.unitSkippedConcepts")}`,
        );
      if (result.relations_skipped.length > 0)
        skipNotes.push(
          `${result.relations_skipped.length} ${t("repositoryMenu.unitSkippedRelations")}`,
        );
      if (result.diagrams_skipped.length > 0)
        skipNotes.push(
          `${result.diagrams_skipped.length} ${t("repositoryMenu.unitSkippedDiagrams")}`,
        );
      // Relaties die een mens nodig hebben (C260 Appendix E.4 + onze
      // matrix-toets). Die stonden alleen in het antwoord en bereikten
      // niemand — zonder deze regel is melden hetzelfde als zwijgen.
      if (result.relations_needing_attention && result.relations_needing_attention.length > 0)
        skipNotes.push(
          `${result.relations_needing_attention.length} ${t("repositoryMenu.unitAttentionRelations")}`,
        );
      const unknownNotes: string[] = [];
      if (result.unknown_element_types.length > 0)
        unknownNotes.push(
          `${t("repositoryMenu.unknownElements")}: ${result.unknown_element_types.join(", ")}`,
        );
      if (result.unknown_relationship_types.length > 0)
        unknownNotes.push(
          `${t("repositoryMenu.unknownRelationships")}: ${result.unknown_relationship_types.join(", ")}`,
        );
      const tail = [...skipNotes, ...unknownNotes].join("; ");
      setLastResult(
        `✓ ${t("repositoryMenu.ameffPrefix")}: ${parts.join(", ") || t("repositoryMenu.zeroItems")}${tail ? ` — ${tail}` : ""}.`,
      );
      setOpen(false);
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
      // Foldertree (in Base-layout) en WP-selector luisteren naar dit
      // event en refreshen hun lokale state (folders + concepten in
      // Base). Zonder dit blijven nieuw-aangemaakte folders + objecten
      // onzichtbaar tot een handmatige hard refresh.
      window.dispatchEvent(new CustomEvent("ae:workpackages-changed"));
      // Open de review-modal wanneer de import gaten heeft die de
      // modelleur moet bevestigen — heuristic-Labels of geclassificeerde
      // views (zelfde gedrag als Settings → Export/Import → AMEFF).
      const hasLabels = result.labels && result.labels.length > 0;
      const hasDiagrams = result.diagrams && result.diagrams.length > 0;
      if (hasLabels || hasDiagrams) {
        setAmeffLabels(result.labels ?? []);
        setAmeffDiagrams(result.diagrams ?? []);
        setAmeffAmbiguous(result.ambiguous ?? []);
        setAmeffReviewBranchId(branchId);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(t("repositoryMenu.ameffFailed", { msg }));
      // Refresh OOK na een gefaalde import. De worker kan een partial-
      // write achterlaten (concepts wel, diagrams niet) als 'ie midway
      // crasht of het CPU-budget overschrijdt. Zonder deze refresh
      // hangt de UI op de pre-import state terwijl D1 een tussen-state
      // heeft — gebruiker ziet "lege" WP en weet niet wat 'r gelandet is.
      if (workspaceId && branchId) {
        await useWorkspaceData
          .getState()
          .hydrate(workspaceId, branchId)
          .catch(() => {
            // Hydrate kan ook falen (network down). Niet escaleren — de
            // alert van de import-fout is al getoond.
          });
      }
    } finally {
      setBusy(null);
    }
  };

  const handleJsonPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    setBusy("json");
    try {
      const snapOk = await snapshotFirst(t("repositoryMenu.snapPreJson"));
      if (!snapOk) return;
      const text = await file.text();
      const payload = JSON.parse(text) as unknown;

      // Eerst aankondigen. Werkt er niemand anders in dit workpackage, dan is
      // er niets aan te kondigen en gaat 'ie meteen door; werkt er wél iemand,
      // dan krijgt die de aftelling en begint de import pas als die op nul
      // staat. Zie `docs/architecture/gelijktijdigheid.md` §7.
      // Eerst droog draaien: dezelfde beslissingen, geen schrijfactie. Wat
      // eruit komt krijgt de aanvrager te zien vóór er iemand anders een
      // aftelling op z'n scherm krijgt.
      const preview = await api.dryRunImport(workspaceId, branchId, payload);
      const uitkomst = await announceThenWait(workspaceId, branchId, {
        kind: "import",
        label: file.name,
        ...(workpackageId ? { workpackageIds: [workpackageId] } : {}),
        impact: impactFromPreview(preview),
        preview,
        askPreview: vraagOmAkkoord(file.name),
      });
      if (!uitkomst.go) {
        setLastResult(t("repositoryMenu.importCancelled"));
        return;
      }

      let result: Awaited<ReturnType<typeof api.importWorkspace>>;
      try {
        result = await api.importWorkspace(workspaceId, branchId, payload);
      } finally {
        await finishOperation(workspaceId, branchId, uitkomst.operationId);
      }
      const parts: string[] = [];
      if (result.concepts_inserted > 0)
        parts.push(`${result.concepts_inserted} ${t("repositoryMenu.unitConcepts")}`);
      if (result.relations_inserted > 0)
        parts.push(`${result.relations_inserted} ${t("repositoryMenu.unitRelations")}`);
      if (result.diagrams_inserted > 0)
        parts.push(`${result.diagrams_inserted} ${t("repositoryMenu.unitDiagrams")}`);
      if (result.view_states_written > 0)
        parts.push(`${result.view_states_written} ${t("repositoryMenu.unitViewStates")}`);
      if (result.folders_created > 0)
        parts.push(`${result.folders_created} ${t("repositoryMenu.unitFoldersCreated")}`);
      const updateNotes: string[] = [];
      if (result.concepts_updated > 0)
        updateNotes.push(`${result.concepts_updated} ${t("repositoryMenu.unitUpdatedConcepts")}`);
      if (result.relations_updated > 0)
        updateNotes.push(`${result.relations_updated} ${t("repositoryMenu.unitUpdatedRelations")}`);
      if (result.diagrams_updated > 0)
        updateNotes.push(`${result.diagrams_updated} ${t("repositoryMenu.unitUpdatedDiagrams")}`);
      if (result.concepts_reused > 0)
        updateNotes.push(`${result.concepts_reused} ${t("repositoryMenu.unitReusedConcepts")}`);
      const skipNotes: string[] = [];
      if (result.relations_skipped > 0)
        skipNotes.push(`${result.relations_skipped} ${t("repositoryMenu.unitSkippedRelations")}`);
      if (result.imported_with_suffix.length > 0)
        skipNotes.push(`${result.imported_with_suffix.length} ${t("repositoryMenu.unitSuffix")}`);
      const tail = [...updateNotes, ...skipNotes].join("; ");
      setLastResult(
        `✓ ${t("repositoryMenu.jsonPrefix")}: ${parts.join(", ") || t("repositoryMenu.zeroItems")}${tail ? ` — ${tail}` : ""}.`,
      );
      setOpen(false);
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
      // Refresh ook Foldertree/Base-layout (zie comment bij AMEFF-handler).
      window.dispatchEvent(new CustomEvent("ae:workpackages-changed"));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      window.alert(t("repositoryMenu.jsonFailed", { msg }));
      // Refresh OOK na fail (partial-write-safe — zie hydrate-comment op AMEFF-handler hierboven).
      if (workspaceId && branchId) {
        await useWorkspaceData
          .getState()
          .hydrate(workspaceId, branchId)
          .catch(() => {});
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative" ref={ref}>
      {/* De droge run, vóór er iemand anders een aftelling op z'n scherm
          krijgt. Annuleren hier laat alles onaangeroerd. */}
      {previewVraag && (
        <OperationPreviewDialog
          preview={previewVraag.preview}
          label={previewVraag.label}
          onConfirm={() => previewVraag.beantwoord(true)}
          onCancel={() => previewVraag.beantwoord(false)}
        />
      )}
      {/* Niet tonen zolang er op de gebruiker gewacht wordt: er wordt dan
          niets geladen, en het legt zich over het venster dat 'ie moet lezen. */}
      <ImportingModal visible={(busy === "json" || busy === "ameff") && !previewVraag} />
      <ImportingModal
        visible={busy === "translate"}
        message={t("importing.updatingTranslations")}
      />
      <ImportingModal visible={busy === "dedupe"} message={t("importing.mergingDuplicates")} />
      <ImportingModal visible={busy === "autoNest"} message={t("importing.linkingContainers")} />
      <button
        type="button"
        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 font-semibold text-(--ld-text) hover:bg-(--ld-raised) disabled:opacity-40"
        disabled={!enabled}
        onClick={() => setOpen((v) => !v)}
        title={`${t("repositoryMenu.button")} (F8)`}
        aria-keyshortcuts="F8"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {t("repositoryMenu.button")} {busy ? "…" : "▾"}
      </button>
      {wpSettingsOpen && workspaceId && (
        <WorkpackageSettingsDialog
          workspaceId={workspaceId}
          workpackageId={workpackageId}
          onClose={() => setWpSettingsOpen(false)}
        />
      )}
      {open && enabled && (
        <div className="ledger absolute right-0 z-50 mt-1 max-h-[calc(100vh-4rem)] w-[360px] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-(--ld-control) bg-(--ld-surface) p-1.5 shadow-lg">
          <MenuEntry
            indent={false}
            title={t("wpSettings.menu")}
            desc={t("wpSettings.menuDesc")}
            onClick={() => {
              setWpSettingsOpen(true);
              setOpen(false);
            }}
          />
          <MenuGroup label={t("repositoryMenu.groupView")} />
          <MenuEntry
            title={t("repositoryMenu.glossaryTitle")}
            desc={t("repositoryMenu.glossaryDesc")}
            onClick={() => {
              openGlossary();
              setOpen(false);
            }}
          />
          <MenuEntry
            title={t("repositoryMenu.typeExplorerTitle")}
            desc={t("repositoryMenu.typeExplorerDesc")}
            onClick={() => {
              setTypeExplorerOpen(true);
              setOpen(false);
            }}
          />
          <MenuGroup label={t("repositoryMenu.groupSnapshots")} hidden={readOnly} />
          <MenuEntry
            title={t("repositoryMenu.snapshotMakeTitle")}
            desc={t("repositoryMenu.snapshotMakeDesc")}
            hidden={readOnly}
            onClick={() => void makeSnapshotManual()}
          />
          <MenuEntry
            title={t("repositoryMenu.snapshotRestoreTitle")}
            desc={t("repositoryMenu.snapshotRestoreDesc")}
            hidden={readOnly}
            onClick={() => void openRestoreModal()}
          />
          <MenuGroup
            label={t("repositoryMenu.groupCleanup")}
            note={t("repositoryMenu.groupCleanupNote")}
            hidden={readOnly}
          />
          <MenuEntry
            title={t("repositoryMenu.dedupeTitle")}
            desc={t("repositoryMenu.dedupeDesc")}
            hidden={readOnly}
            onClick={() => void dedupeAll()}
          />
          <MenuEntry
            title={t("repositoryMenu.autoNestTitle")}
            desc={t("repositoryMenu.autoNestDesc")}
            hidden={readOnly}
            onClick={() => void autoDetectNesting()}
          />
          <MenuEntry
            title={t("repositoryMenu.linkAttrTitle")}
            desc={t("repositoryMenu.linkAttrDesc")}
            hidden={readOnly}
            onClick={() => void linkAttributesToConcepts()}
          />
          <MenuEntry
            title={t("repositoryMenu.translateTitle")}
            desc={t("repositoryMenu.translateDesc")}
            hidden={readOnly}
            onClick={() => void updateTranslations()}
          />
          <MenuGroup label={t("repositoryMenu.groupExchange")} hidden={readOnly} />
          <MenuEntry
            title={t("repositoryMenu.exportAmeffTitle")}
            desc={t("repositoryMenu.exportAmeffDesc")}
            hidden={readOnly}
            disabled={busy === "export-ameff"}
            onClick={() => void exportAmeff()}
          />
          <MenuEntry
            title={t("repositoryMenu.ameffTitle")}
            desc={t("repositoryMenu.ameffDesc")}
            hidden={readOnly}
            onClick={() => ameffInputRef.current?.click()}
          />
          <MenuEntry
            title={t("repositoryMenu.exportJsonTitle")}
            desc={t("repositoryMenu.exportJsonDesc")}
            hidden={readOnly}
            disabled={busy === "export-json"}
            onClick={() => void exportJson()}
          />
          <MenuEntry
            title={t("repositoryMenu.jsonTitle")}
            desc={t("repositoryMenu.jsonDesc")}
            hidden={readOnly}
            onClick={() => jsonInputRef.current?.click()}
          />
          {lastResult && (
            <div
              role="status"
              className="mx-1.5 mt-1.5 rounded-md bg-(--ld-positive-bg) px-2.5 py-2 text-(--ld-positive-ink) text-xs"
            >
              {lastResult}
            </div>
          )}
        </div>
      )}
      {restoreOpen && (
        // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via Escape + knoppen, klik-buiten is muis-gemak
        <div
          className="ledger fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40"
          onClick={() => setRestoreOpen(false)}
          role="presentation"
        >
          <div
            className="w-[480px] max-w-[90vw] rounded-md border border-(--ld-control) bg-(--ld-surface) p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h2 className="mb-1 font-semibold text-(--ld-text) text-sm">
              {t("repositoryMenu.snapshotRestoreModalTitle")}
            </h2>
            <p className="mb-3 text-(--ld-muted) text-xs">
              {restoreScope ? `Scope: ${restoreScope}` : ""}
            </p>
            {restoreItems.length === 0 ? (
              <p className="text-(--ld-muted) text-xs">
                {t("repositoryMenu.snapshotRestoreModalEmpty")}
              </p>
            ) : (
              <ul className="max-h-[400px] divide-y divide-(--ld-rule) overflow-y-auto rounded border border-(--ld-border)">
                {restoreItems.map((s) => (
                  <li key={s.id} className="flex items-center justify-between px-3 py-2 text-xs">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-(--ld-text)">{s.label}</div>
                      <div className="text-xs text-(--ld-muted)">
                        {new Date(s.created_at).toLocaleString("nl-NL", { hour12: false })} ·{" "}
                        {s.counts.concepts}c / {s.counts.relations}r / {s.counts.diagrams}d
                      </div>
                      {s.restorable && s.restorable.status !== "yes" && (
                        <div
                          className={`text-xs ${s.restorable.status === "never" ? "text-(--ld-negative)" : "text-(--ld-notice-ink)"}`}
                        >
                          {s.restorable.status === "never"
                            ? t("repositoryMenu.restoreNever")
                            : t("repositoryMenu.restoreLater")}
                          {s.restorable.reason ? ` ${s.restorable.reason}` : ""}
                        </div>
                      )}
                    </div>
                    {s.restorable?.status === "never" ? (
                      <button
                        type="button"
                        className="ml-3 rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-negative-ink) hover:bg-(--ld-negative-bg) disabled:opacity-40"
                        disabled={!!busy}
                        onClick={() => void removeSnapshot(s.id, s.label)}
                      >
                        {t("repositoryMenu.snapshotCleanup")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="ml-3 rounded border border-transparent bg-(--ld-surface) px-2 py-0.5 text-(--ld-positive-ink) hover:bg-(--ld-positive-bg) disabled:opacity-40"
                        disabled={!!busy || s.restorable?.status === "later"}
                        onClick={() => void doRestore(s.id, s.label)}
                      >
                        {t("repositoryMenu.snapshotRestoreModalConfirm")}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 text-right">
              <button
                type="button"
                className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
                onClick={() => setRestoreOpen(false)}
              >
                {t("repositoryMenu.snapshotRestoreModalCancel")}
              </button>
            </div>
          </div>
        </div>
      )}
      <input
        ref={ameffInputRef}
        type="file"
        accept=".xml,.archimate"
        className="hidden"
        onChange={(e) => void handleAmeffPick(e)}
      />
      <input
        ref={jsonInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => void handleJsonPick(e)}
      />
      {(ameffLabels.length > 0 || ameffDiagrams.length > 0 || ameffAmbiguous.length > 0) &&
        ameffReviewBranchId &&
        workspaceId && (
          <AmeffLabelReview
            labels={ameffLabels}
            diagrams={ameffDiagrams}
            ambiguous={ameffAmbiguous}
            workspaceId={workspaceId}
            branchId={ameffReviewBranchId}
            onClose={() => {
              setAmeffLabels([]);
              setAmeffDiagrams([]);
              setAmeffAmbiguous([]);
              setAmeffReviewBranchId(null);
            }}
          />
        )}
    </div>
  );
}

/** Compact human-readable byte-size, gebruikt door export-handlers
 *  voor de success-melding. Gedupliceerd patroon uit
 *  `settings/PortabilitySettings.tsx` — bewust niet gehoist naar een
 *  shared helper omdat deze hier <10 regels is en de import-graph
 *  klein houden voor een leaf-component van de header. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** Eén regel in het Repository-menu: titel met een korte uitleg eronder.
 *  Ingesprongen onder de kop van z'n groep, tenzij `indent={false}`. */
function MenuEntry({
  title,
  desc,
  onClick,
  hidden,
  disabled,
  indent = true,
}: {
  title: string;
  desc: string;
  onClick: () => void;
  hidden?: boolean;
  disabled?: boolean;
  indent?: boolean;
}) {
  return (
    <button
      type="button"
      hidden={hidden}
      disabled={disabled}
      onClick={onClick}
      className={`block rounded-md px-2.5 py-2 text-left transition-colors hover:bg-(--ld-raised) disabled:opacity-50 disabled:hover:bg-transparent ${indent ? "ml-6 w-[calc(100%-1.5rem)]" : "w-full"}`}
    >
      <span className="block font-medium text-(--ld-text) text-[13px]">{title}</span>
      <span className="mt-0.5 block text-pretty text-(--ld-muted) text-xs leading-[1.45]">
        {desc}
      </span>
    </button>
  );
}

/** Kop boven een groep in het Repository-menu (vet, met een lijn eronder),
 *  met optioneel één regel uitleg. */
function MenuGroup({ label, note, hidden }: { label: string; note?: string; hidden?: boolean }) {
  return (
    <div
      hidden={hidden}
      className="mx-1.5 mt-3.5 mb-1 border-(--ld-border) border-b px-1 pb-1.5 font-semibold text-(--ld-text) text-[13px]"
    >
      {label}
      {note && <span className="font-normal text-(--ld-muted) text-xs"> · {note}</span>}
    </div>
  );
}
